import { string, object, mixed, array } from 'yup';
import { type TReminderTemplate } from '@src/shared/types/reminder-template';
import {
  REMINDER_TRIGGER_TYPES,
  REMINDER_TYPES,
  type TReminder,
} from '@src/shared/types/reminder';
import { RULE_TYPES } from '@src/shared/types/rule';

const RULE_TYPES_WITH_CHOICES: string[] = [
  RULE_TYPES.trueFalse,
  RULE_TYPES.applicationSubmission,
];

export const createReminderInitialValues = ({
  reminder,
  reminderTemplate,
}: {
  reminder?: TReminder;
  reminderTemplate?: TReminderTemplate;
}) => {
  return {
    attachments: [] as TReminder['attachments'],
    name: '',
    content: '',
    subject: '',
    notification_channel: '' as TReminder['notification_channel'],
    trigger_type: '' as TReminder['trigger_type'],
    type: '' as TReminder['type'],
    variables: {} as Record<string, string>,
    date: '',
    frequency: 0,
    frequency_count: 0,
    rule: '',
    stages: [] as number[],
    question: '',
    is_rule_achieved: '',
    rule_choices: [] as string[],
    rule_type: '' as string,
    view: '' as unknown as TReminder['view'],
    template: '' as unknown as TReminder['template'],
    flight_type: '' as TReminder['flight_type'],
    submission_date_type: '' as TReminder['submission_date_type'],

    ...createInitialValueFromExistingReminder(reminder),
    ...createInitialValueFromExistingReminderTemplate(reminderTemplate),
  };
};

const createInitialValueFromExistingReminder = (reminder?: TReminder) => {
  if (!reminder) return {};

  return {
    attachments: reminder.attachments,
    name: reminder.name,
    content: reminder.content,
    subject: reminder.subject,
    notification_channel: reminder.notification_channel,
    type: reminder.type,
    trigger_type: reminder.trigger_type,
    variables: reminder.variables || {},
    date: reminder.date,
    frequency: reminder.frequency,
    frequency_count: reminder.frequency_count,
    rule: String(reminder.rule?.id || ''),
    stages:
      reminder.stages?.map((stage) => stage.id) ??
      (reminder.stage?.id ? [reminder.stage.id] : []),
    question: String(
      typeof reminder.question === 'object' &&
        reminder.question !== null &&
        'id' in reminder.question
        ? (reminder.question as { id: number }).id
        : (reminder.question ?? '')
    ),
    is_rule_achieved: reminder.is_rule_achieved ? 'ACHIEVED' : 'NOT_ACHIEVED',
    rule_choices: reminder.rule_choices ?? [],
    rule_type: reminder.rule?.type ?? '',
    template: reminder.template,
    view: reminder.view,
    flight_type: reminder.flight_type,
    submission_date_type: reminder.submission_date_type,
  };
};

const createInitialValueFromExistingReminderTemplate = (
  reminderTemplate?: TReminderTemplate
) => {
  if (!reminderTemplate) return {};

  return {
    // attachments: reminderTemplate.attachments,
    name: reminderTemplate.name,
    content: reminderTemplate.content,
    notification_channel: reminderTemplate.channel,
    variables: reminderTemplate.variables || {},
    template: reminderTemplate,
  };
};

export const reminderFormSchema = object().shape({
  attachments: array().of(mixed()),
  name: string().required('Name is required'),
  content: string().required('Content is required'),
  subject: string().nullable(),
  view: mixed().nullable(),
  notification_channel: string().required('Notification channel is required'),
  type: string().required('Type is required'),
  trigger_type: string().nullable(),
  frequency: string().when(['type', 'trigger_type'], {
    is: (type: string, triggerType: string) => {
      const isProgramDateType = type === REMINDER_TYPES.programDateRelated;
      const isOtherRuleType = type === REMINDER_TYPES.otherRules;
      const isBeforeAfterTrigger = triggerType !== REMINDER_TRIGGER_TYPES.on;

      return isOtherRuleType || (isProgramDateType && isBeforeAfterTrigger);
    },
    then: (schema) => schema.required('Please enter days'),
    otherwise: (schema) => schema.nullable(),
  }),
  frequency_count: string().when(['type', 'frequency'], {
    is: (type: string, frequency: string) =>
      type === REMINDER_TYPES.otherRules && frequency,
    then: (schema) => schema.required('Please enter frequency count'),
    otherwise: (schema) => schema.nullable(),
  }),
  date: string().when(['type'], {
    is: (type: string) => type === REMINDER_TYPES.specificDate,
    then: (schema) => schema.required('Please enter date'),
    otherwise: (schema) => schema.nullable(),
  }),
  flight_type: string().when(['type'], {
    is: (type: string) => type === REMINDER_TYPES.flightInfo,
    then: (schema) => schema.required('Please enter flight type'),
    otherwise: (schema) => schema.nullable(),
  }),
  submission_date_type: string().when(['type'], {
    is: (type: string) => type === REMINDER_TYPES.submissionDate,
    then: (schema) => schema.required('Please enter submission date type'),
    otherwise: (schema) => schema.nullable(),
  }),

  variables: object(),
  rule: string(),
  is_rule_achieved: string(),
  rule_type: string().nullable(),
  rule_choices: array()
    .of(string())
    .nullable()
    .when(['type', 'rule_type'], {
      is: (type: string, ruleType: string) =>
        type === REMINDER_TYPES.otherRules &&
        RULE_TYPES_WITH_CHOICES.includes(ruleType),
      then: (schema) => schema.min(1, 'Please select at least one choice'),
    }),
  template: mixed().nullable(),
});

export type TReminderFormValues = ReturnType<
  typeof createReminderInitialValues
>;
